import type { TFunction } from 'i18next';

import type { CalendarEvent, CalendarRecurrence, DayOfWeek } from '../types/Calendar';

const DAY_OF_WEEK_VALUES: DayOfWeek[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const DEFAULT_CALENDAR_RECURRENCE = (): CalendarRecurrence => ({
  frequency: 'none',
  interval: 1,
  daysOfWeek: [],
});

export const dayOfWeekFromDate = (value: Date): DayOfWeek => DAY_OF_WEEK_VALUES[value.getDay()];

export const buildDefaultWeeklyRecurrence = (startAt: Date): CalendarRecurrence => ({
  frequency: 'weekly',
  interval: 1,
  daysOfWeek: [dayOfWeekFromDate(startAt)],
});

export const recurrenceFromEvent = (event?: Pick<CalendarEvent, 'recurrence'> | null): CalendarRecurrence => {
  if (!event?.recurrence || event.recurrence.frequency === 'none') {
    return DEFAULT_CALENDAR_RECURRENCE();
  }

  return {
    frequency: event.recurrence.frequency,
    interval: event.recurrence.interval || 1,
    daysOfWeek: event.recurrence.daysOfWeek || [],
    dayOfMonth: event.recurrence.dayOfMonth,
    endDate: event.recurrence.endDate,
    maxOccurrences: event.recurrence.maxOccurrences,
  };
};

export const buildRecurrencePayload = (recurrence: CalendarRecurrence): CalendarRecurrence => {
  if (recurrence.frequency === 'none') {
    return { frequency: 'none', interval: 1, daysOfWeek: [] };
  }

  return {
    frequency: recurrence.frequency,
    interval: recurrence.interval,
    daysOfWeek: recurrence.frequency === 'weekly' ? recurrence.daysOfWeek : [],
    dayOfMonth: recurrence.frequency === 'monthly' ? recurrence.dayOfMonth : undefined,
    endDate: recurrence.endDate || undefined,
    maxOccurrences: recurrence.maxOccurrences || undefined,
  };
};

export const hasActiveRecurrence = (recurrence: CalendarRecurrence) => recurrence.frequency !== 'none';

export const formatRecurrenceSummary = (
  recurrence: CalendarRecurrence | undefined | null,
  t: TFunction<'calendar'>,
): string => {
  if (!recurrence || recurrence.frequency === 'none') {
    return t('recurrenceNone', { defaultValue: 'Does not repeat' });
  }

  const interval = recurrence.interval || 1;
  const unit = t(`recurrenceIntervalUnit.${recurrence.frequency}`, {
    count: interval,
    defaultValue: recurrence.frequency,
  });
  let summary = t('recurrenceSummary', {
    defaultValue: 'Every {{interval}} {{unit}}',
    interval,
    unit,
  });

  if (recurrence.frequency === 'weekly' && recurrence.daysOfWeek.length > 0) {
    const days = recurrence.daysOfWeek
      .map((day: DayOfWeek) => t(`weekdaysShort.${day}`, { defaultValue: day.slice(0, 3) }))
      .join(', ');
    summary = `${summary} (${days})`;
  }

  if (recurrence.endDate) {
    summary = `${summary} · ${t('recurrenceUntil', { defaultValue: 'until {{date}}', date: recurrence.endDate })}`;
  } else if (recurrence.maxOccurrences) {
    summary = `${summary} · ${t('recurrenceForOccurrences', {
      defaultValue: '{{count}} times',
      count: recurrence.maxOccurrences,
    })}`;
  }

  return summary;
};
